import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import FileCheckIcon from "@redotech/redo-icons/file-check-02.svg";
import FileCheckFilledIcon from "@redotech/redo-icons/file-check-02_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  WorkOrderViewCountsContext,
  WorkOrderViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/work-orders/work-order-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { OrderManagementSuiteStatusesContext } from "../../../../redo-suite/order-management-cloud";
import { useIsChildRouteActive } from "./use-child-route-active";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const WorkOrdersItem = memo(function WorkOrdersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { wmsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const workOrderViewsContext = useContext(WorkOrderViewsContext);
  const workOrderViewCountsContext = useContext(WorkOrderViewCountsContext);

  const wmsBaseUrl = `${routes.shippingBaseUrl}/wms`;
  const workOrdersBaseUrl = `${wmsBaseUrl}/work-orders`;

  const isAnyChildActive = useIsChildRouteActive(workOrdersBaseUrl, []);

  const allViews = useMemo(() => {
    return [...(workOrderViewsContext?.views ?? [])];
  }, [workOrderViewsContext]);

  const allFolders = useMemo(() => {
    return [...(workOrderViewsContext?.folders ?? [])];
  }, [workOrderViewsContext]);

  const viewsWithoutFolders = useMemo(() => {
    return allViews.filter((view) => !view.folderId);
  }, [allViews]);

  const foldersWithViews = useMemo<FolderWithView<"work-orders">[]>(() => {
    return allFolders.map((folder) => {
      return {
        folder,
        views: allViews.filter((view) => view.folderId === folder._id),
      };
    });
  }, [allFolders, allViews]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: workOrdersBaseUrl,
      viewCounts: workOrderViewCountsContext?.viewCounts,
      folderCounts: workOrderViewCountsContext?.folderCounts,
      hideZeroNotifications: true,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    workOrdersBaseUrl,
    workOrderViewCountsContext?.viewCounts,
    workOrderViewCountsContext?.folderCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    workOrderViewsContext?.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"work-orders">(
      flattenedItems,
      allViews,
      allFolders,
    );
    workOrderViewsContext?.updateOrdering(views, folders);
  });

  if (!wmsEnabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isAnyChildActive}
      FilledIcon={FileCheckFilledIcon}
      friendlyName={t("Work orders", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="work-orders"
      UnfilledIcon={FileCheckIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <WorkOrdersAddViewItem href={`${workOrdersBaseUrl}/create`} />
      <WorkOrdersCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const WorkOrdersAddViewItem = memo(function WorkOrdersAddViewItem({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={t("+ Add new view", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="work-orders-create-view"
    />
  );
});

const WorkOrdersCreateFolderItem = memo(function WorkOrdersCreateFolderItem() {
  const { t } = useTranslation();
  const workOrderViewsContext = useContext(WorkOrderViewsContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          workOrderViewsContext?.openCreateFolderModal();
        },
      }}
      friendlyName={t("+ Add new folder", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="work-orders-create-folder"
    />
  );
});
